import type { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';

import { ausrehabApi as baseApi } from '../baseApi';
import type { SupplierProps } from '../../models';

interface LocationQueryResponse {
	coordinates: [string, string];
}

interface UpdateTestingCentrePayload extends SupplierProps {
	testIds: number[];
}

interface AddTestingCentrePayload extends SupplierProps {
	testIds: number[];
}

interface GetTestingCentreArgs {
	location: string;
	testIds: number[];
}

interface GetTestingCentresResponse extends SupplierProps {
	distanceInMeters: number;
}

const testingCentreApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getSupplier: builder.query({
			query: (id) => ({
				url: `suppliers/${id}`,
			}),
			providesTags: ['Suppliers'],
		}),

		// biome-ignore lint/suspicious/noConfusingVoidType: prevent explicitly providing undefined in query
		getSuppliers: builder.query<GetTestingCentresResponse[], GetTestingCentreArgs | void>({
			async queryFn(
				_arg,
				_queryApi,
				_extraOptions,
				fetchWithBQ,
			): Promise<QueryReturnValue<GetTestingCentresResponse[]>> {
				if (_arg === undefined) {
					const allSuppliers = await fetchWithBQ({
						url: 'suppliers',
					});
					return allSuppliers as QueryReturnValue<GetTestingCentresResponse[]>;
				}

				const { location, testIds } = _arg;

				const coordinates = (await fetchWithBQ({
					url: 'locations',
					params: {
						location,
					},
				})) as QueryReturnValue<LocationQueryResponse>;

				const [lng, lat] = coordinates?.data?.coordinates ?? [];

				const data = (await fetchWithBQ({
					url: 'suppliers',
					params: {
						lng,
						lat,
						testIds,
						// should return full list
						// limit: 3,
					},
				})) as QueryReturnValue<GetTestingCentresResponse[]>;

				return data;
			},
			providesTags: ['Suppliers'],
		}),

		delSupplier: builder.mutation({
			query: (id) => ({
				url: `suppliers/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Suppliers'],
		}),

		updateSupplier: builder.mutation<SupplierProps, UpdateTestingCentrePayload>({
			async queryFn(payload, _queryApi, _extraOptions, baseQuery): Promise<QueryReturnValue<SupplierProps>> {
				const { address } = payload;

				const coordinates = (await baseQuery({
					url: 'locations',
					params: {
						location: address,
					},
				})) as QueryReturnValue<LocationQueryResponse>;

				const [lng, lat] = coordinates?.data?.coordinates ?? [];

				const data = (await baseQuery({
					url: `suppliers/${payload.id}`,
					method: 'PUT',
					body: {
						...payload,
						longitude: lng,
						latitude: lat,
					},
				})) as QueryReturnValue<SupplierProps>;

				return data;
			},
			invalidatesTags: ['Suppliers'],
		}),

		addSupplier: builder.mutation<SupplierProps, AddTestingCentrePayload>({
			async queryFn(payload, _queryApi, _extraOptions, baseQuery): Promise<QueryReturnValue<SupplierProps>> {
				const { address } = payload;

				const coordinates = (await baseQuery({
					url: 'locations',
					params: {
						location: address,
					},
				})) as QueryReturnValue<LocationQueryResponse>;

				const [lng, lat] = coordinates?.data?.coordinates ?? [];

				const data = (await baseQuery({
					url: 'suppliers',
					method: 'POST',
					body: {
						...payload,
						longitude: lng,
						latitude: lat,
					},
				})) as QueryReturnValue<SupplierProps>;

				return data;
			},
			invalidatesTags: ['Suppliers'],
		}),
	}),
	overrideExisting: false,
});

export const {
	useGetSuppliersQuery,
	useGetSupplierQuery,
	useAddSupplierMutation,
	useDelSupplierMutation,
	useUpdateSupplierMutation,
} = testingCentreApi;
